import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

const phoneSchema = z.string().min(10).max(20).regex(/^[0-9+()\-\s]+$/);
const orderStatusSchema = z.enum(["pending", "processing", "shipped", "delivered", "cancelled"]);

export const getAdminStatus = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { isAdmin: false, canClaimFirstAdmin: false };

  const { data: role } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", user.id)
    .eq("role", "admin")
    .maybeSingle();

  const { count } = await supabase
    .from("user_roles")
    .select("id", { count: "exact", head: true })
    .eq("role", "admin");

  return { 
    isAdmin: !!role, 
    canClaimFirstAdmin: !role && (count === 0) 
  };
};

export const claimFirstAdminRole = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Não autenticado");

  const { error } = await supabase.from("user_roles").insert({ 
    user_id: user.id, 
    role: "admin" 
  });
  
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const validateCustomerPhone = async (phone: string) => {
  // In a pure SPA without a server proxy, we can't securely validate WhatsApp via UazAPI 
  // without exposing tokens. For now, we return a basic valid status.
  return { exists: true, name: null };
};

export const createPublicOrder = async (data: {
  customerName: string;
  customerPhone: string;
  items: Array<{ id: string; quantity: number }>;
}) => {
  const ids = data.items.map(i => i.id);
  const { data: products } = await supabase
    .from("products")
    .select("id, name, price")
    .in("id", ids);

  if (!products) throw new Error("Produtos não encontrados");

  const productMap = new Map(products.map(p => [p.id, p]));
  const orderItems = data.items.map(item => {
    const product = productMap.get(item.id);
    if (!product) throw new Error("Produto inválido");
    return {
      id: product.id,
      name: product.name,
      quantity: item.quantity,
      price: Number(product.price)
    };
  });

  const totalPrice = orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const { data: order, error } = await supabase
    .from("orders")
    .insert({
      customer_name: data.customerName,
      customer_phone: data.customerPhone.replace(/\D/g, ""),
      items: orderItems,
      total_price: totalPrice,
      status: "pending"
    })
    .select("id")
    .single();

  if (error) throw new Error(error.message);
  
  // Note: WhatsApp notifications disabled in pure SPA mode to protect API keys.
  // Use Supabase Edge Functions for secure notifications.
  
  return { id: order.id };
};

export const updateAdminOrderStatus = async (data: {
  orderId: string;
  status: "pending" | "processing" | "shipped" | "delivered" | "cancelled";
  trackingCode?: string;
}) => {
  const { error } = await supabase
    .from("orders")
    .update({ 
      status: data.status, 
      tracking_code: data.trackingCode || null 
    })
    .eq("id", data.orderId);

  if (error) throw new Error(error.message);
  return { ok: true };
};

// Placeholder functions for UazAPI (would require a secure proxy/Edge Function)
export const checkAdminUazStatus = async (instanceName: string) => {
  return { connected: false, status: "disconnected (requires server proxy)" };
};

export const getAdminUazQr = async (instanceName: string) => {
  return { status: "disconnected", qrCode: null };
};

export const logoutAdminUaz = async (instanceName: string) => {
  return { ok: true };
};
